import { Action, createReducer, on } from '@ngrx/store';
import * as actions from './course-search.actions';
import { CourseDetails, CourseSearch } from '../../models/course-search.models';
import {
	SingleClearingCourseSearchUni,
	SingleCourseSearchUni,
	UniversityCoursePagination,
} from '../../models/single-uni-course-search.models';
import { ClearingCourseSearch } from '../../models/clearing-course-search.models';
import { fetchPaginatedUniCourses, fetchUniCourses } from './course-search.actions';
import { CourseSearchFilterPill } from '../../models/course-search-filter.models';
import {
	CourseSearchPayload,
	UniversityCourseSearchPayload,
} from '../../models/course-search-payload.models';
import {
	COURSE_SEARCH_DEFAULT_PAYLOAD,
	UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
} from '../../static/course-search-payload.properties';

export const _STATE_COURSE_SEARCH_FEATURE_KEY = 'stateCourseSearchV2';

export interface CourseSearchState {
	courses: CourseSearch | null;
	clearingCourses: ClearingCourseSearch | null;

	uni: SingleCourseSearchUni | null;
	clearingUni: SingleClearingCourseSearchUni | null;

	courseSearchPayload: CourseSearchPayload;
	uniCourseSearchPayload: UniversityCourseSearchPayload;
	clearingCourseSearchPayload: CourseSearchPayload;
	clearingUniCourseSearchPayload: UniversityCourseSearchPayload;
	filterPills: CourseSearchFilterPill[];
	filterToBeRemoved: CourseSearchFilterPill | null;

	isLoading: boolean;
	isLoadingPagination: boolean;

	error: any;
}

export interface StateCourseSearchPartialState {
	readonly [_STATE_COURSE_SEARCH_FEATURE_KEY]: CourseSearchState;
}

export const initialState: CourseSearchState = {
	courses: null,
	clearingCourses: null,

	uni: null,
	clearingUni: null,

	courseSearchPayload: { ...COURSE_SEARCH_DEFAULT_PAYLOAD },
	uniCourseSearchPayload: { ...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD },

	clearingCourseSearchPayload: { ...COURSE_SEARCH_DEFAULT_PAYLOAD },
	clearingUniCourseSearchPayload: { ...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD },
	filterPills: [],
	filterToBeRemoved: null,

	isLoading: false, //shows the loading cards (no courses remain)
	isLoadingPagination: false, // shows animation within load more button (courses remain in view)

	error: null,
};

const courseSearchReducer = createReducer(
	initialState,

	on(actions.fetchCourses, fetchUniCourses, (state) => ({
		...state,
		isLoading: true,
		isLoadingPagination: false,
		error: null,
	})),

	on(actions.fetchPaginatedCourses, fetchPaginatedUniCourses, (state) => ({
		...state,
		isLoading: false,
		isLoadingPagination: true,
		error: null,
	})),

	// courses
	on(actions.updateCourses, (state, { courses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			clearingCourses: null,
			courses: {
				...courses,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedCourses, (state, { courses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			clearingCourses: null,
			courses: {
				...courses,
				data: [...(state.courses?.data || []), ...courses.data],
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updateUniCourses, (state, { uni }) => {
		return {
			...state,
			courses: null,
			clearingUni: null,
			clearingCourses: null,
			uni: {
				...state.uni,
				...uni,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedUniCourses, (state, { uni }) => {
		const courses = [
			...(state.uni?.courses?.data ?? []),
			...(uni.courses?.data ?? []),
		];
		return {
			...state,
			courses: null,
			clearingUni: null,
			clearingCourses: null,
			uni: {
				...state.uni,
				...uni,
				courses: {
					...state.uni?.courses,
					...uni.courses,
					data: courses,
				},
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	// clearing
	on(actions.updateClearingCourses, (state, { clearingCourses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			courses: null,
			clearingCourses: {
				...state.clearingCourses,
				...clearingCourses,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updateClearingCourseSearchUni, (state, { clearingUni, mergeCourses }) => {
		const newCourses = getUniCourses(clearingUni);
		const oldCourses = getUniCourses(state.clearingUni);
		const allUniCourses = mergeCourses
			? combineCourses(newCourses, oldCourses)
			: newCourses;
		return {
			...state,
			uni: null,
			clearingCourses: null,
			courses: null,
			clearingUni: {
				...state.clearingUni,
				...clearingUni,
				courses: {
					...(state.clearingUni?.courses as UniversityCoursePagination),
					...clearingUni?.courses,
					data: allUniCourses,
				},
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updateCourseSearchPayload, (state, { payload }) => {
		const courseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			universityCourseSearchPayload: {
				...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
			},
			courseSearchPayload: {
				...state.courseSearchPayload,
				...courseSearchPayload,
			},
		};
	}),

	on(actions.updateUniCourseSearchPayload, (state, { payload }) => {
		const uniCourseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			courseSearchPayload: { ...COURSE_SEARCH_DEFAULT_PAYLOAD },
			uniCourseSearchPayload: {
				...state.uniCourseSearchPayload,
				...uniCourseSearchPayload,
			},
		};
	}),

	on(actions.updateFilterPills, (state, { pills }) => {
		return {
			...state,
			filterPills: pills,
		};
	}),

	on(actions.removeFilterPill, (state, { pill }) => {
		return {
			...state,
			filterToBeRemoved: pill,
		};
	}),

	on(actions.updateCourseSearchError, (state, { error }) => {
		return {
			...state,
			isLoading: false,
			isLoadingPagination: false,
			shouldLoadCourses: false,
			error,
		};
	}),
);

export function reducer(state: CourseSearchState | undefined, action: Action) {
	return courseSearchReducer(state, action);
}

const combineCourses = <T>(newCourses: T[] | null, stateCourses: T[] = []): T[] => {
	if (!newCourses) return stateCourses || [];
	return [...stateCourses, ...newCourses];
};

const getUniCourses = (
	uni: null | SingleCourseSearchUni | SingleClearingCourseSearchUni,
): CourseDetails[] => {
	if (!uni || !uni.courses || !uni.courses.data) return [];

	return uni.courses.data;
};
